// src/components/Terms.js
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

const Terms = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;

    // allow render/layout to settle
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, [location]);

  return (
    <Wrap>
      <Inner>
        <Top>
          <H1>Terms and Conditions</H1>
          <Meta>
            Catching Gold Diggers
            <Dot>•</Dot>
            Last updated: <strong>[2026]</strong>
          </Meta>

          <Intro>
            These Terms and Conditions (the <strong>Terms</strong>) govern your access to and use of
            the Catching Gold Diggers website and related services (the <strong>Site</strong> or{" "}
            <strong>Service</strong>). By accessing, viewing, using, or subscribing to the Site, you
            agree to be bound by these Terms. If you do not agree, you must leave the Site
            immediately and you must not use the Service.
          </Intro>

          <Intro>
            These Terms also apply to your subscription membership. Where you are required to click
            a button stating “I agree” (or similar), that action forms your electronic acceptance of
            these Terms.
          </Intro>
        </Top>

        <Section id="operator">
          <H2>1. Operator and Contact Details</H2>
          <P>
            The Site is operated by <strong>[Legal entity name]</strong> (referred to as{" "}
            <strong>Catching Gold Diggers</strong>.
          </P>
          <List>
            <li>
              Registered office: <strong>[Registered office address]</strong>
            </li>
            <li>
              Email: <strong>[support@catchinggolddiggers.com]</strong>
            </li>
          </List>
        </Section>

        <Section id="adults-only">
          <H2>2. Adults Only (18+)</H2>
          <P>
            The Site contains sexually explicit adult content and is intended only for individuals
            who are at least <strong>18 years old</strong> (or the age of majority in your
            jurisdiction, if higher).
          </P>
          <P>By entering the Site, you confirm that:</P>
          <List>
            <li>you are at least 18 years old</li>
            <li>you are legally permitted to view adult content where you access the Site</li>
            <li>you will not allow any minor to access the Site using your device or account</li>
          </List>
          <P>You must not attempt to bypass any age verification or access control measures.</P>
        </Section>

        <Section id="content">
          <H2>3. Content and Materials</H2>
          <P>
            The Site contains videos, images, text, graphics, software, data, and other materials
            (collectively, <strong>Materials</strong>). Materials may be owned by us or licensed to
            us. We may update, remove, or change Materials and the design or operation of the Site
            at any time.
          </P>
        </Section>

        <Section id="no-minors">
          <H2>4. No Content Involving Minors</H2>
          <P>
            We maintain a strict zero tolerance policy for any content involving minors. All
            performers shown on the Site are represented to be{" "}
            <strong>18 years of age or older</strong> at the time of production.
          </P>
          <P>
            If you are seeking any form of content involving minors, you must leave the Site
            immediately. If you believe any content on the Site is illegal or depicts minors, report
            it immediately to <strong>[report@catchinggolddiggers.com]</strong> with the URL and relevant
            details. We will investigate promptly and cooperate with lawful requests from relevant
            authorities.
          </P>
        </Section>

        <Section id="accounts">
          <H2>5. Accounts</H2>
          <P>
            Some features may require an account. You agree to provide accurate, complete, and up to
            date information, and to keep your login credentials confidential. You are responsible
            for all activity on your account until you notify us of compromise.
          </P>
          <P>
            We may suspend or terminate accounts where we reasonably believe there is unauthorised
            use, fraud, chargeback abuse, or a breach of these Terms.
          </P>
        </Section>

        {/* Main subscription section */}
        <Section id="subscription">
          <H2>6. Subscription, Payments, and Billing</H2>

          <H3>6.1 Subscription only</H3>
          <P>
            Catching Gold Diggers is subscription based. Your subscription grants access to the
            subscriber areas and content made available to subscribers during the subscription
            period.
          </P>

          <H3>6.2 Payment processing</H3>
          <P>
            Payments may be processed by authorised third party payment processors (for example,
            CCBill, Segpay, or others). When you subscribe, you authorise the processor (and us
            where applicable) to charge your selected payment method.
          </P>

          <H3>6.3 Automatic renewal</H3>
          <P>
            Your subscription renews automatically at the interval displayed at checkout unless you
            cancel before the renewal date. The renewal charge will be taken using the payment
            method on file.
          </P>

          <H3>6.4 Pricing</H3>
          <P>
            Prices are displayed before you complete purchase. You are responsible for any bank or
            card fees charged by your provider.
          </P>

          {/* Anchor target for Billing Descriptor */}
          <AnchorBlock id="billing-descriptor">
            <H3>6.5 Billing descriptor</H3>
            <P>
              Your card statement will display a descriptor such as{" "}
              <strong>[Billing descriptor]</strong> (or similar). If you do not recognise a charge,
              contact support immediately.
            </P>
          </AnchorBlock>

          <H3>6.6 Billing errors</H3>
          <P>
            If you believe you have been billed incorrectly, contact support as soon as possible
            with your account email, date of charge, and details. We will investigate promptly.
          </P>
        </Section>

        {/* Anchor target for Cancellation */}
        <Section id="cancellation">
          <H2>7. Cancellation</H2>
          <P>
            You can cancel your subscription at any time via your account settings (if available) or
            by contacting support. Cancellation stops future renewals. Unless stated otherwise at
            checkout, you will retain access until the end of your current paid subscription period.
          </P>
        </Section>

        {/* Anchor target for Refunds */}
        <Section id="refunds">
          <H2>8. Refunds</H2>
          <P>
            Digital services are generally non refundable once access is provided, except where UK
            consumer laws require a refund (for example, where the service is not delivered as
            described and we cannot resolve it within a reasonable time).
          </P>
          <P>
            We may consider refunds or credits in limited cases such as duplicate billing,
            unauthorised transactions (subject to investigation and processor rules), or technical
            faults preventing access that we cannot resolve.
          </P>
          <P>
            For support, email <strong>[support@catchinggolddiggers.com]</strong>.
          </P>
        </Section>

        <Section id="license">
          <H2>9. Limited Licence and Permitted Use</H2>
          <P>
            Subject to these Terms, we grant you a limited, personal, non exclusive, non transferable
            licence to access and view Materials for your private, non commercial use only.
          </P>
          <P>You must not:</P>
          <List>
            <li>
              copy, record, reproduce, redistribute, re upload, broadcast, or publicly display
              Materials
            </li>
            <li>share your account or resell access</li>
            <li>remove watermarks or proprietary notices</li>
            <li>use Materials for commercial purposes without written permission</li>
          </List>
          <P>Any unauthorised use terminates your licence immediately.</P>
        </Section>

        <Section id="prohibited">
          <H2>10. Prohibited Conduct</H2>
          <P>You agree you will not:</P>
          <List>
            <li>use the Site for illegal purposes</li>
            <li>attempt to hack, bypass paywalls, scrape content, or interfere with Site security</li>
            <li>use automated tools to extract content or data without permission</li>
            <li>upload malware or harmful code</li>
            <li>harass, threaten, or abuse any person, or impersonate others</li>
          </List>
        </Section>

        <Section id="ip">
          <H2>11. Intellectual Property</H2>
          <P>
            All Site content, trademarks, branding, and Materials are owned by or licensed to
            Catching Gold Diggers and are protected by applicable intellectual property laws.
            Nothing in these Terms transfers ownership to you.
          </P>
        </Section>

        <Section id="third-party-links">
          <H2>12. Links to Third Party Websites</H2>
          <P>
            The Site may contain links to third party sites. We do not control third party sites and
            are not responsible for their content, policies, or practices. You access third party
            links at your own risk.
          </P>
        </Section>

        {/* Anchor target for Report Content */}
        <Section id="report-content">
          <H2>13. Complaints, Reporting, and Removal Requests</H2>

          <H3>13.1 Reporting content</H3>
          <P>
            To report content you believe is illegal, non consensual, infringing, or otherwise
            violates these Terms, email <strong>[report@catchinggolddiggers.com]</strong> with the URL, a
            description, and any supporting information.
          </P>

          <H3>13.2 If you are depicted in content</H3>
          <P>
            If you are depicted in content on the Site and want to request review or removal, email{" "}
            <strong>[report@catchinggolddiggers.com]</strong> with the URL, your contact details, and your
            request. We may request verification to prevent fraudulent requests and to protect
            creators and performers.
          </P>
        </Section>

        <Section id="copyright">
          <H2>14. Copyright Complaints</H2>
          <P>
            If you believe your copyright is being infringed, email{" "}
            <strong>[dmca@catchinggolddiggers.com]</strong> with your details, identification of the
            work, the URL of the allegedly infringing material, and a good faith statement that the
            use is not authorised.
          </P>
        </Section>

        <Section id="termination">
          <H2>15. Termination</H2>
          <P>
            You may stop using the Site at any time. We may suspend or terminate your access (with
            or without notice) if you breach these Terms, we suspect fraud or unlawful activity, we
            are required to do so by law or payment providers, or we discontinue the Site or parts
            of it.
          </P>
        </Section>

        <Section id="disclaimers">
          <H2>16. Disclaimers</H2>
          <P>
            The Site and Materials are provided on an “as is” and “as available” basis. We do not
            guarantee uninterrupted access, error free operation, or that the Site will meet your
            expectations.
          </P>
          <P>
            Nothing in these Terms limits any consumer rights that cannot legally be excluded under
            UK law.
          </P>
        </Section>

        <Section id="liability">
          <H2>17. Limitation of Liability</H2>
          <P>
            To the fullest extent permitted by law, we will not be liable for indirect or
            consequential loss, loss of profits, loss of data, or business interruption arising from
            your use of the Site.
          </P>
          <P>
            Where liability cannot be excluded under UK law, we limit liability to the maximum
            extent permitted.
          </P>
        </Section>

        <Section id="indemnity">
          <H2>18. Indemnity</H2>
          <P>
            You agree to indemnify and hold us harmless against claims, losses, and expenses
            (including reasonable legal fees) arising from your breach of these Terms or misuse of
            the Site.
          </P>
        </Section>

        <Section id="privacy">
          <H2>19. Privacy</H2>
          <P>
            Your use of the Site is also governed by our{" "}
            <StyledLink to="/privacy">Privacy Policy</StyledLink>.
          </P>
        </Section>

        <Section id="changes">
          <H2>20. Changes to These Terms</H2>
          <P>
            We may update these Terms from time to time. The “Last updated” date will change
            accordingly. Your continued use of the Site after changes means you accept the updated
            Terms.
          </P>
        </Section>

        <Section id="governing-law">
          <H2>21. Governing Law and Jurisdiction (UK)</H2>
          <P>
            These Terms are governed by the laws of <strong>England and Wales</strong>. The courts
            of England and Wales will have jurisdiction, except where mandatory consumer laws in
            your location require otherwise.
          </P>
        </Section>

        <BottomNav>
          <BackLink to="/">Back to Home</BackLink>
        </BottomNav>
      </Inner>
    </Wrap>
  );
};

export default Terms;

/* ---------- styles ---------- */

const Wrap = styled.div`
  background: #000;
  color: #fff;
  min-height: 100vh;
  padding: 60px 0;
`;

const Inner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0 80px;

  @media (max-width: 868px) {
    padding: 0 35px;
  }

  @media (max-width: 568px) {
    padding: 0 20px;
  }
`;

const Top = styled.div`
  margin-bottom: 26px;
`;

const H1 = styled.h1`
  margin: 0 0 10px;
  font-size: 36px;
  font-weight: 900;
  letter-spacing: -0.4px;

  @media (max-width: 568px) {
    font-size: 28px;
  }
`;

const Meta = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
`;

const Dot = styled.span`
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.35);
`;

const Intro = styled.p`
  margin: 14px 0 0;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
`;

/* Key: scroll-margin-top prevents sticky header covering the target */
const Section = styled.section`
  scroll-margin-top: 110px;
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

/* Used to anchor to a subsection inside a Section */
const AnchorBlock = styled.div`
  scroll-margin-top: 110px;
`;

const H2 = styled.h2`
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 800;
`;

const H3 = styled.h3`
  margin: 14px 0 6px;
  font-size: 14px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
`;

const P = styled.p`
  margin: 0 0 10px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.78);
`;

const List = styled.ul`
  margin: 0 0 10px;
  padding-left: 18px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.65;

  li {
    margin: 6px 0;
  }
`;

const BottomNav = styled.div`
  margin-top: 34px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: flex-start;
`;

const StyledLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  text-decoration: underline;
`;

const BackLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;
